// The back-office header's shared actions (legacy `AppHeaderUserMenu.vue`, `AppHeaderThemeToggle.vue`), used by the
// location shell and the staff shell alike, as legacy's one header served both.
import { DropdownMenu } from 'radix-ui';
import { Moon, Sun, SunMoon } from 'lucide-react';
import type { Appearance } from '@radish/ui-web';
import { useAppearance } from './appearance.tsx';

export const APPEARANCES: readonly { readonly value: Appearance; readonly label: string }[] = [
  { value: 'light', label: 'Light mode' },
  { value: 'dark', label: 'Dark mode' },
  { value: 'auto', label: 'Auto' },
];

/**
 * Legacy shows its header theme menu only to a dev build or the staging back office (`AppHeaderUserMenu.vue`:
 * `isDesktop && (isDev || isStaging)`); production users change the appearance from the account menu instead.
 * Next's staging hosts are `*.radishnext.dev`.
 */
export function showsThemeMenu(hostname: string, devBuild: boolean): boolean {
  return devBuild || hostname === 'radishnext.dev' || hostname.endsWith('.radishnext.dev');
}

// Legacy's header actions: 36 px, a 1 px border, 8 px corners, secondary text at rest, green while their menu is open.
export const HEADER_ACTION = 'inline-flex size-9 items-center justify-center rounded-q border border-line bg-surface text-text-2 data-[state=open]:border-primary data-[state=open]:text-primary';

const THEME_ICONS = { light: Sun, dark: Moon, auto: SunMoon } as const;

/** Legacy `AppHeaderThemeToggle.vue`: the current mode's icon opens Light mode / Dark mode / Auto. */
export function ThemeMenu() {
  const { appearance, setAppearance } = useAppearance();
  const label = APPEARANCES.find((a) => a.value === appearance)?.label ?? 'Light mode';
  const Icon = THEME_ICONS[appearance];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger aria-label={label} title={label} data-testid="theme-menu" className={HEADER_ACTION}>
        <Icon size={20} strokeWidth={1.5} aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={0} className="z-[3000] min-w-40 rounded-q border border-line bg-menu py-1 text-[14px] text-menu-ink-body shadow-lg dark:border-transparent dark:shadow-none">
          <DropdownMenu.RadioGroup value={appearance} onValueChange={(v) => setAppearance(v as Appearance)}>
            {APPEARANCES.map((a) => {
              const OptionIcon = THEME_ICONS[a.value];
              return (
                <DropdownMenu.RadioItem key={a.value} value={a.value}
                  className="flex h-12 cursor-pointer items-center gap-0 px-4 outline-none data-[highlighted]:bg-menu-item-hover data-[state=checked]:text-primary">
                  <span className="inline-flex w-8 shrink-0"><OptionIcon size={18} strokeWidth={1.5} aria-hidden /></span>
                  {a.label}
                </DropdownMenu.RadioItem>
              );
            })}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
